import React, { useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Select,
  Spin,
  App,
  Dropdown,
  Form,
  Upload,
  Tooltip,
} from "antd";
import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getAgentByIdThunk,
  createBaseKnowledgeThunk,
  getToolsThunk,
  updateAgentThunk,
  getBaseKnowledgeThunk,
} from "../../store/actions/agent.action";
import { AGENTS_VOICE, IAgent } from "../../models/agent.model";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as CheckCircle } from "../../assets/icons/checkCircle.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import type { MenuProps } from "antd";
import * as Yup from "yup";
import {
  getDirectionDisplayValue,
  getDirectionSelectOptions,
} from "../../utils/directionOptions";
import VoicePreviewButton from "../../components/VoicePreviewButton";
import { TOOL_TYPE_GROUPS } from "../../models/agent.model";

const { Option } = Select;
const OptGroup = Select.OptGroup;

const VARIABLE_REGEX = /{{{([\s\S]*?)}}}/g;
// System variable presets removed

const AgentDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.agent);
  const { user } = useAppSelector((state) => state.auth);
  const [editSection, setEditSection] = React.useState<
    null | "basic" | "settings" | "messages" | "knowledge"
  >(null);
  const [agent, setAgent] = React.useState<IAgent | null>(null);
  const [form] = Form.useForm();
  const [changedFields, setChangedFields] = React.useState<Record<string, any>>(
    {}
  );
  const [greetingVariables, setGreetingVariables] = React.useState<string[]>(
    []
  );
  const [systemVariables, setSystemVariables] = React.useState<string[]>([]);
  const [voicemailVariables, setVoicemailVariables] = React.useState<string[]>(
    []
  );
  const [endCallVariables, setEndCallVariables] = React.useState<string[]>([]);
  const [greetingCaret, setGreetingCaret] = React.useState<{
    start: number;
    end: number;
  } | null>(null);
  const [systemCaret, setSystemCaret] = React.useState<{
    start: number;
    end: number;
  } | null>(null);
  const [voicemailCaret, setVoicemailCaret] = React.useState<{
    start: number;
    end: number;
  } | null>(null);
  const [endCallCaret, setEndCallCaret] = React.useState<{
    start: number;
    end: number;
  } | null>(null);
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);
  const [isSectionValid, setIsSectionValid] = React.useState(true);
  const [tools, setTools] = React.useState<any[]>([]);

  const groupedTools = React.useMemo(() => {
    const groups: Record<string, any[]> = {
      general: [],
      customize: [],
      internal: [],
    };

    tools.forEach((tool) => {
      const rawType = tool.type as string;
      const type = String(rawType).toLowerCase();
      if (groups[type]) {
        groups[type].push(tool);
      }
    });

    return groups;
  }, [tools]);
  const [knowledgeFiles, setKnowledgeFiles] = React.useState<File[]>([]);
  const [isKbDragging, setIsKbDragging] = React.useState(false);
  const [baseKnowledge, setBaseKnowledge] = React.useState<any[]>([]);
  const [selectedBaseKnowledge, setSelectedBaseKnowledge] = React.useState<
    string[]
  >([]);
  const [currentVoiceName, setCurrentVoiceName] = React.useState<string>("");

  const getVoiceNameByValue = (voiceValue?: string) => {
    if (!voiceValue) return "";
    return AGENTS_VOICE.find((voice) => voice.value === voiceValue)?.name || "";
  };

  // Helper function to filter out endCall from agent data
  const getAgentFormData = (agentData: IAgent | null) => {
    if (!agentData) return {};
    const voiceFromConfigs =
      (agentData as any)?.voice_configs?.voice ?? (agentData as any)?.voice;
    const greetingFromConfigs =
      (agentData as any)?.voice_configs?.greeting_message ??
      (agentData as any)?.greeting_message;
    const systemFromConfigs =
      (agentData as any)?.system_message;
    const voicemailFromConfigs =
      (agentData as any)?.voice_configs?.voicemail_message ??
      (agentData as any)?.voicemail_message;
    const endCallFromConfigs =
      (agentData as any)?.voice_configs?.ending_message;
    return {
      ...agentData,
      // Flatten BE `voice_configs.*` back into the existing form fields
      voice: voiceFromConfigs,
      greeting_message: greetingFromConfigs,
      system_message: systemFromConfigs,
      voicemail_message:
        voicemailFromConfigs || (agentData as any).voicemailMessage || "",
      ending_message: endCallFromConfigs || "",
      knowledge_base_ids: (agentData.knowledge_base_ids || []).map(
        (item: any) => item._id
      ),
      tool: (agentData.tool || []).filter(
        (fn: string | number) => String(fn) !== "endCall"
      ),
    };
  };

  // Validation schemas for each section
  const validationSchemas = {
    basic: Yup.object().shape({
      agent_name: Yup.string().required(
        t("agents.agentForm.validation.agentNameRequired")
      ),
      // agent_description is optional
      // direction is read-only in basic section, so it's not validated
    }),
    settings: Yup.object().shape({
      voice: Yup.string().required(
        t("agents.agentForm.validation.voiceRequired")
      ),
      // Make tools optional
      tool: Yup.array().of(Yup.mixed()).notRequired(),
    }),
    messages: Yup.object().shape({
      greeting_message: Yup.string().required(
        t("agents.agentForm.validation.greetingMessageRequired")
      ),
      system_message: Yup.string().required(
        t("agents.agentForm.validation.systemMessageRequired")
      ),
    }),
  };

  const fetchBaseKnowledge = async () => {
    try {
      const res = await dispatch(getBaseKnowledgeThunk()).unwrap();
      setBaseKnowledge(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Validate current section
  const validateCurrentSection = async () => {
    if (!editSection) return true;

    try {
      const currentSchema =
        validationSchemas[editSection as keyof typeof validationSchemas];
      if (currentSchema) {
        // Validate ALL form values for the current section, not just changed fields
        const allFormValues = form.getFieldsValue();
        await currentSchema.validate(allFormValues, { abortEarly: false });
        setIsSectionValid(true);
        return true;
      }
      return true;
    } catch (error) {
      setIsSectionValid(false);
      return false;
    }
  };

  const fetchTools = async () => {
    try {
      const res = await dispatch(getToolsThunk()).unwrap();
      setTools(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  // Knowledge Base helpers (mirror CreateAgentModal step 4 behavior)
  const handleKnowledgeFileChange = (files: File[]) => {
    setKnowledgeFiles(files);
    if (editSection) validateCurrentSection();
  };
  const addKnowledgeFiles = (incoming: File[]) => {
    if (!incoming || incoming.length === 0) return;
    const signature = (f: File) =>
      `${f.name}|${f.size}|${(f as any).lastModified ?? ""}`;
    const existingMap = new Map(knowledgeFiles.map((f) => [signature(f), f]));
    for (const f of incoming) {
      const sig = signature(f);
      if (!existingMap.has(sig)) existingMap.set(sig, f);
    }
    const merged = Array.from(existingMap.values());
    handleKnowledgeFileChange(merged);
  };
  const removeKnowledgeFile = (index: number) => {
    const next = knowledgeFiles.filter((_, i) => i !== index);
    handleKnowledgeFileChange(next);
  };

  const fetchAgent = async () => {
    try {
      const res = await dispatch(
        getAgentByIdThunk({ id: id as string })
      ).unwrap();
      const apiAgent: IAgent = res.data;
      const flattened: IAgent = {
        ...apiAgent,
        voice: (apiAgent as any)?.voice_configs?.voice ?? apiAgent.voice,
        greeting_message:
          (apiAgent as any)?.voice_configs?.greeting_message ??
          apiAgent.greeting_message,
        system_message:
          apiAgent.system_message,
        voicemail_message:
          (apiAgent as any)?.voice_configs?.voicemail_message ??
          apiAgent.voicemail_message,
      };
      setAgent(flattened);
      setCurrentVoiceName(getVoiceNameByValue(flattened.voice));
      setSelectedBaseKnowledge(
        (apiAgent.knowledge_base_ids || []).map((item: any) => item._id)
      );

      // Set form values, filtering out endCall from tools
      form.setFieldsValue(getAgentFormData(flattened));

      // Parse variables from agent data
      if (flattened.greeting_message) {
        setGreetingVariables(parseVariables(flattened.greeting_message));
      }
      if (flattened.system_message) {
        setSystemVariables(parseVariables(flattened.system_message));
      }
      if (flattened.voicemail_message) {
        setVoicemailVariables(parseVariables(flattened.voicemail_message));
      }
      if (flattened.voice_configs?.ending_message) {
        setEndCallVariables(parseVariables(flattened.voice_configs.ending_message));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAgent();
    }
  }, [id]);

  // Parse variables from text - matching CreateAgentModal exactly
  const parseVariables = (text?: string): string[] => {
    if (!text) return [];
    const vars: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = VARIABLE_REGEX.exec(text)) !== null) {
      const name = (match[1] || "").trim();
      vars.push(name || "");
    }
    return Array.from(new Set(vars));
  };

  // Update variable chips when form values change - matching CreateAgentModal exactly
  const updateVariableChips = () => {
    const {
      greeting_message,
      system_message,
      voicemail_message,
      ending_message,
    } = form.getFieldsValue([
      "greeting_message",
      "system_message",
      "voicemail_message",
      "ending_message",
    ]);
    setGreetingVariables(parseVariables(greeting_message));
    setSystemVariables(parseVariables(system_message));
    setVoicemailVariables(parseVariables(voicemail_message));
    setEndCallVariables(parseVariables(ending_message));
  };

  // Get textarea element from antd ref - matching CreateAgentModal exactly
  const getTextAreaEl = (ref: any): HTMLTextAreaElement | null => {
    if (!ref) return null;
    return (
      ref?.resizableTextArea?.textArea ||
      ref?.textArea ||
      ref?.input ||
      ref ||
      null
    );
  };

  // Escape string for regex - matching CreateAgentModal exactly
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Remove variable from text - matching CreateAgentModal exactly
  const removeVariableFromText = (text: string, varName: string): string => {
    const safeVarName = escapeRegExp(varName);
    const token = new RegExp(`{{{\\s*${safeVarName}\\s*}}}`, "g");
    let result = text.replace(token, "");
    result = result.replace(/\s{2,}/g, " ").replace(/\s+([,.!?;:])/g, "$1");
    return result.trim();
  };

  type MessageFieldName =
    | "greeting_message"
    | "system_message"
    | "voicemail_message"
    | "ending_message";

  const getRefForField = (fieldName: MessageFieldName) => {
    if (fieldName === "greeting_message") return greetingRef.current;
    if (fieldName === "system_message") return systemRef.current;
    if (fieldName === "voicemail_message") return voicemailRef.current;
    return endCallRef.current;
  };

  const setCaretForField = (
    fieldName: MessageFieldName,
    caret: { start: number; end: number } | null
  ) => {
    if (fieldName === "greeting_message") setGreetingCaret(caret);
    else if (fieldName === "system_message") setSystemCaret(caret);
    else if (fieldName === "voicemail_message") setVoicemailCaret(caret);
    else setEndCallCaret(caret);
  };

  const getCaretForField = (fieldName: MessageFieldName) => {
    if (fieldName === "greeting_message") return greetingCaret;
    if (fieldName === "system_message") return systemCaret;
    if (fieldName === "voicemail_message") return voicemailCaret;
    return endCallCaret;
  };

  const getAgentMessageValue = (fieldName: MessageFieldName) => {
    if (fieldName === "ending_message") {
      return agent?.voice_configs?.ending_message;
    }
    return agent?.[fieldName];
  };

  // Sanitize variable spaces - matching CreateAgentModal exactly
  const sanitizeVariableSpaces = (
    fieldName: MessageFieldName,
    incomingValue: string,
    caretStart: number | null | undefined
  ) => {
    let value = incomingValue;

    let newCaret = caretStart ?? null;
    if (newCaret !== null) {
      const regex = VARIABLE_REGEX;
      let match: RegExpExecArray | null;
      let removedBefore = 0;
      while ((match = regex.exec(value)) !== null) {
        const fullStart = match.index;
        const inner = match[1] ?? "";
        const innerStart = fullStart + 3;
        const innerEnd = innerStart + inner.length;
        const leadingSpaces = inner.match(/^\s+/)?.[0].length ?? 0;
        const trailingSpaces = inner.match(/\s+$/)?.[0].length ?? 0;
        const totalRemoved = leadingSpaces + trailingSpaces;
        if (totalRemoved === 0) continue;

        if (newCaret > innerEnd) {
          removedBefore += totalRemoved;
          continue;
        }
        if (newCaret >= innerStart && newCaret <= innerEnd) {
          if (newCaret <= innerStart + leadingSpaces) {
            newCaret = innerStart;
          } else if (newCaret > innerEnd - trailingSpaces) {
            newCaret = innerEnd - trailingSpaces;
          } else {
            newCaret = newCaret - leadingSpaces;
          }
        }
      }
      if (newCaret !== null) newCaret = newCaret - removedBefore;
    }

    value = value.replace(
      VARIABLE_REGEX,
      (_m, inner: string) => `{{{${(inner || "").trim()}}}}`
    );

    form.setFieldsValue({ [fieldName]: value });
    // variable chips will update via onValuesChange

    // Track changed fields
    if (agent && getAgentMessageValue(fieldName) !== value) {
      setChangedFields((prev) => ({ ...prev, [fieldName]: value }));
    } else {
      setChangedFields((prev) => {
        const newChangedFields = { ...prev };
        delete newChangedFields[fieldName];
        return newChangedFields;
      });
    }

    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    if (el && newCaret !== null) {
      requestAnimationFrame(() => {
        try {
          el.setSelectionRange(newCaret!, newCaret!);
        } catch {}
      });
    }
  };

  // Remember caret position - matching CreateAgentModal exactly
  const rememberCaret = (
    fieldName: MessageFieldName,
    e?: React.SyntheticEvent<HTMLTextAreaElement>
  ) => {
    const target = e?.currentTarget as HTMLTextAreaElement | undefined;
    const el = target || getTextAreaEl(getRefForField(fieldName));
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    setCaretForField(fieldName, { start, end });
  };

  // Insert variable at caret - matching CreateAgentModal exactly
  const insertVariableAtCaret = (
    fieldName: MessageFieldName,
    variableName?: string
  ) => {
    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    if (!el) return;

    const currentValue: string = form.getFieldValue(fieldName) || "";

    const isFocused = document.activeElement === el;
    const remembered = getCaretForField(fieldName);
    const start = isFocused
      ? el.selectionStart ?? currentValue.length
      : remembered?.start ?? currentValue.length;
    const end = isFocused ? el.selectionEnd ?? start : remembered?.end ?? start;
    const prevChar = start > 0 ? currentValue.charAt(start - 1) : "";
    const nextChar = end < currentValue.length ? currentValue.charAt(end) : "";

    // Only add spaces if there are actual characters and they're not whitespace
    const needsSpaceBefore =
      start > 0 && currentValue.length > 0 && !/\s/.test(prevChar);
    const needsSpaceAfter =
      end < currentValue.length &&
      currentValue.length > 0 &&
      !/\s/.test(nextChar);

    const coreToken = `{{{${variableName ?? ""}}}}`;
    const token =
      (needsSpaceBefore ? " " : "") + coreToken + (needsSpaceAfter ? " " : "");
    const newValue =
      currentValue.slice(0, start) + token + currentValue.slice(end);

    form.setFieldsValue({ [fieldName]: newValue });
    // Ensure chips update immediately after programmatic insert
    updateVariableChips();

    // Track changed fields
    if (agent && getAgentMessageValue(fieldName) !== newValue) {
      setChangedFields((prev) => ({ ...prev, [fieldName]: newValue }));
    } else {
      setChangedFields((prev) => {
        const newChangedFields = { ...prev };
        delete newChangedFields[fieldName];
        return newChangedFields;
      });
    }

    const newCaretPos = variableName
      ? start +
        (needsSpaceBefore ? 1 : 0) +
        coreToken.length +
        (needsSpaceAfter ? 1 : 0)
      : start + (needsSpaceBefore ? 1 : 0) + 3;
    requestAnimationFrame(() => {
      el.focus();
      try {
        el.setSelectionRange(newCaretPos, newCaretPos);
      } catch {}
      setCaretForField(fieldName, { start: newCaretPos, end: newCaretPos });
    });
  };

  // Get dropdown menu for field - matching CreateAgentModal exactly
  const getDropdownMenuForField = (
    fieldName: MessageFieldName
  ): MenuProps => {
    const items: MenuProps["items"] = [
      {
        type: "group",
        label: t("agents.agentForm.customVariables"),
        children: [
          { key: "__add_new__", label: t("agents.agentForm.addVariable") },
        ],
      },
    ];
    return {
      items,
      onClick: ({ key }) => {
        if (key === "__add_new__") insertVariableAtCaret(fieldName);
        else insertVariableAtCaret(fieldName, String(key));
      },
    };
  };

  // Remove variable - matching CreateAgentModal exactly
  const handleRemoveVariable = (
    fieldName: MessageFieldName,
    variableName: string
  ) => {
    const currentValue: string = form.getFieldValue(fieldName) || "";
    const newValue = removeVariableFromText(currentValue, variableName);
    form.setFieldsValue({ [fieldName]: newValue });
    updateVariableChips();

    // Track changed fields
    if (agent && getAgentMessageValue(fieldName) !== newValue) {
      setChangedFields((prev) => ({ ...prev, [fieldName]: newValue }));
    } else {
      setChangedFields((prev) => {
        const newChangedFields = { ...prev };
        delete newChangedFields[fieldName];
        return newChangedFields;
      });
    }
  };

  // Refs for textareas
  const greetingRef = React.useRef<any>(null);
  const systemRef = React.useRef<any>(null);
  const voicemailRef = React.useRef<any>(null);
  const endCallRef = React.useRef<any>(null);

  // Handle form values change to update variable chips and validate
  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (
      Object.prototype.hasOwnProperty.call(changedValues, "greeting_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "system_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "voicemail_message") ||
      Object.prototype.hasOwnProperty.call(changedValues, "ending_message")
    ) {
      setGreetingVariables(parseVariables(allValues.greeting_message));
      setSystemVariables(parseVariables(allValues.system_message));
      setVoicemailVariables(parseVariables(allValues.voicemail_message));
      setEndCallVariables(parseVariables(allValues.ending_message));
    }

    // Validate current section when values change
    if (editSection) {
      validateCurrentSection();
    }
  };

  // Handle input change for non-textarea fields
  const handleInputChange = (field: string, value: any) => {
    // Filter out endCall from tools
    if (field === "tool" && Array.isArray(value)) {
      value = value.filter((fn: string | number) => String(fn) !== "endCall");
    }

    // Track changed fields
    if (agent && agent[field as keyof IAgent] !== value) {
      setChangedFields((prev) => ({ ...prev, [field]: value }));
    } else {
      setChangedFields((prev) => {
        const newChangedFields = { ...prev };
        delete newChangedFields[field];
        return newChangedFields;
      });
    }

    if (field === "voice") {
      setCurrentVoiceName(getVoiceNameByValue(value));
    }

    // Validate current section when input changes
    if (editSection) {
      validateCurrentSection();
    }
  };

  // Handle textarea change with variable tracking - matching CreateAgentModal exactly
  const handleTextAreaChange = (
    fieldName: MessageFieldName,
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const ref = getRefForField(fieldName);
    const el = getTextAreaEl(ref);
    const caret = el?.selectionStart ?? null;
    sanitizeVariableSpaces(fieldName, e.target.value, caret);

    // Validate current section when textarea changes
    if (editSection) {
      validateCurrentSection();
    }
  };

  const handleSave = async () => {
    try {
      // Validate the current section before saving - validate ALL form data, not just changedFields
      const currentSchema =
        validationSchemas[editSection as keyof typeof validationSchemas];
      if (currentSchema) {
        // Get all form values for the current section, not just changed fields
        const allFormValues = form.getFieldsValue();
        await currentSchema.validate(allFormValues, { abortEarly: false });
      }

      // Start with changed primitive fields, then normalize to BE schema
      let payload: any = { ...changedFields };
      const allValues = form.getFieldsValue();

      // Only process variables if we're editing the messages section or if message fields were changed
      if (
        editSection === "messages" ||
        changedFields.greeting_message !== undefined ||
        changedFields.system_message !== undefined ||
        changedFields.ending_message !== undefined
      ) {
        const customVariablesObj: { [key: string]: string } = {};
        let customIndex = 1;

        // Process all variables from both textareas
        const allVariables = Array.from(
          new Set([
            ...greetingVariables,
            ...systemVariables,
            ...voicemailVariables,
            ...endCallVariables,
          ])
        );

        allVariables.forEach((variable) => {
          // Treat all variables as custom variables
          customVariablesObj[`custom_variable_${customIndex}`] = variable;
          customIndex++;
        });

        // Add parameters to payload (BE schema: parameters.{system_variables, custom_variables})
        payload.parameters = {
          system_variables: agent?.parameters?.system_variables ?? {},
          custom_variables: customVariablesObj,
        };
      }

      // BE schema stores voice/messages under `voice_configs`
      const isVoiceConfigsTouched =
        editSection === "settings" ||
        editSection === "messages" ||
        changedFields.voice !== undefined ||
        changedFields.greeting_message !== undefined ||
        changedFields.system_message !== undefined ||
        changedFields.voicemail_message !== undefined ||
        changedFields.ending_message !== undefined;

      if (isVoiceConfigsTouched) {
        payload.voice_configs = {
          ...(agent as any)?.voice_configs,
          voice: allValues.voice,
          greeting_message: allValues.greeting_message,
          voicemail_message: allValues.voicemail_message,
          ...(user?.is_super_admin
            ? { ending_message: allValues.ending_message }
            : {}),
        };
        // Remove legacy flat fields from payload if they exist
        delete payload.voice;
        delete payload.greeting_message;
        delete payload.voicemail_message;
        delete payload.ending_message;
      }

      // If saving Knowledge section, upload files and link to agent
      if (editSection === "knowledge") {
        if (knowledgeFiles.length > 0) {
          const uploadResult: any = await dispatch(
            createBaseKnowledgeThunk({
              files: knowledgeFiles,
            })
          ).unwrap();
          const kbId: string[] = uploadResult?.data?.knowledge_base_ids || [];
          if (kbId.length > 0) {
            (payload as any).knowledge_base_ids = [
              ...selectedBaseKnowledge,
              ...kbId,
            ];
          }
        } else {
          (payload as any).knowledge_base_ids = selectedBaseKnowledge;
        }
      }

      await dispatch(updateAgentThunk({ id: id || "", payload })).unwrap();
      if (editSection === "knowledge") {
        await fetchBaseKnowledge();
      }
      await fetchAgent();
      toast.success(
        t("agents.agentDetail.agentUpdatedSuccessfully"),
        5,
        <CheckCircleIcon />
      );
    } catch (error: any) {
      if (error.name === "ValidationError") {
        // Handle validation errors
        const validationErrors = error.inner.reduce((acc: any, err: any) => {
          acc[err.path] = err.message;
          return acc;
        }, {});

        // Set form errors
        form.setFields(
          Object.keys(validationErrors).map((field) => ({
            name: field,
            errors: [validationErrors[field]],
          }))
        );
        toast.error(t("agents.agentDetail.pleaseFixValidationErrors"), 5);
      } else {
        toast.error(t("agents.agentDetail.failedToUpdateAgent"), 5);
        console.log(error);
      }
    } finally {
      setChangedFields({});
      setEditSection(null);
      setKnowledgeFiles([]);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue(getAgentFormData(agent));
    setChangedFields({});
    setEditSection(null);
    setIsSectionValid(true);
    setCurrentVoiceName(getVoiceNameByValue(agent?.voice));

    // Reset variables to original agent data
    if (agent?.greeting_message) {
      setGreetingVariables(parseVariables(agent.greeting_message));
    }
    if (agent?.system_message) {
      setSystemVariables(parseVariables(agent.system_message));
    }
    if (agent?.voicemail_message) {
      setVoicemailVariables(parseVariables(agent.voicemail_message));
    }
    if (agent?.voice_configs?.ending_message) {
      setEndCallVariables(parseVariables(agent.voice_configs.ending_message));
    } else {
      setEndCallVariables([]);
    }
  };

  const handleBack = () => {
    if (editSection && Object.keys(changedFields).length > 0) {
      setShowUnsavedModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleLeave = () => {
    setShowUnsavedModal(false);
    form.setFieldsValue(getAgentFormData(agent));
    setChangedFields({});
    setEditSection(null);
    setCurrentVoiceName(getVoiceNameByValue(agent?.voice));
    setEndCallVariables(
      agent?.voice_configs?.ending_message
        ? parseVariables(agent.voice_configs.ending_message)
        : []
    );
    navigate(-1);
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
  };

  // Update validation state when edit section changes
  React.useEffect(() => {
    if (editSection) {
      validateCurrentSection();
    } else {
      setIsSectionValid(true);
    }
  }, [editSection]);

  React.useEffect(() => {
    fetchTools();
    fetchBaseKnowledge();
  }, []);

  return (
    <Container>
      <BackLink onClick={handleBack}>
        <ArrowLeftOutlined style={{ marginRight: 8 }} />{" "}
        {t("agents.agentDetail.allAgents")}
      </BackLink>
      <Title>
        {agent?.agent_name} <Text>(ID: {agent?._id})</Text>
      </Title>

      <Form
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={agent || {}}
      >
        {/* Basic Information Section */}
        <SectionCard style={{ marginBottom: 24 }}>
          <SectionHeader>
            <SectionTitle>
              {t("agents.agentDetail.basicInformation")}
            </SectionTitle>
            {editSection === "basic" ? (
              <div>
                <StyledButton onClick={handleCancel} style={{ marginRight: 8 }}>
                  {t("common.cancel")}
                </StyledButton>
                <StyledButton
                  $primary
                  onClick={handleSave}
                  disabled={!isSectionValid}
                >
                  {t("common.save")}
                </StyledButton>
              </div>
            ) : (
              <Button
                type="text"
                icon={<EditIcon />}
                onClick={() => {
                  setEditSection("basic");
                  form.setFieldsValue(getAgentFormData(agent));
                  // Reset variables to original agent data when entering edit mode
                  if (agent?.greeting_message) {
                    setGreetingVariables(
                      parseVariables(agent.greeting_message)
                    );
                  }
                  if (agent?.system_message) {
                    setSystemVariables(parseVariables(agent.system_message));
                  }
                  if (agent?.voicemail_message) {
                    setVoicemailVariables(parseVariables(agent.voicemail_message));
                  }
                  if (agent?.voice_configs?.ending_message) {
                    setEndCallVariables(
                      parseVariables(agent.voice_configs.ending_message)
                    );
                  } else {
                    setEndCallVariables([]);
                  }
                }}
              />
            )}
          </SectionHeader>
          {editSection === "basic" ? (
            <>
              <InfoGrid>
                <InfoRow>
                  <Label>{t("agents.agentForm.agentName")}</Label>
                  <Form.Item
                    name="agent_name"
                    style={{ flex: 1, margin: 0 }}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "agents.agentForm.validation.agentNameRequired"
                        ),
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      style={{ flex: 1 }}
                      allowClear
                      placeholder={t("agents.agentForm.enterAgentName")}
                      onChange={(e) =>
                        handleInputChange("agent_name", e.target.value)
                      }
                    />
                  </Form.Item>
                </InfoRow>
                <InfoRow>
                  <Label>{t("agents.agentForm.agentDescription")}</Label>
                  <Form.Item
                    name="agent_description"
                    style={{ flex: 1, margin: 0 }}
                  >
                    <Input
                      size="large"
                      style={{ flex: 1 }}
                      allowClear
                      placeholder={t("agents.agentForm.enterDescription")}
                      onChange={(e) =>
                        handleInputChange("agent_description", e.target.value)
                      }
                    />
                  </Form.Item>
                </InfoRow>
                <InfoRow>
                  <Label>{t("agents.agentForm.direction")}</Label>
                  <Value>
                    {agent?.direction
                      ? getDirectionDisplayValue(agent.direction, t)
                      : ""}
                  </Value>
                </InfoRow>
              </InfoGrid>
            </>
          ) : (
            <InfoGrid>
              <InfoRow>
                <Label>{t("agents.agentForm.agentName")}</Label>
                <Value>{agent?.agent_name}</Value>
              </InfoRow>
              <InfoRow>
                <Label>{t("agents.agentForm.agentDescription")}</Label>
                <Value>{agent?.agent_description}</Value>
              </InfoRow>
              <InfoRow>
                <Label>{t("agents.agentForm.direction")}</Label>
                <Value>
                  {agent?.direction
                    ? getDirectionDisplayValue(agent.direction, t)
                    : ""}
                </Value>
              </InfoRow>
            </InfoGrid>
          )}
        </SectionCard>

        {/* Agent Settings Section */}
        <SectionCard style={{ marginBottom: 24 }}>
          <SectionHeader>
            <SectionTitle>{t("agents.agentDetail.agentSettings")}</SectionTitle>
            {editSection === "settings" ? (
              <div>
                <StyledButton onClick={handleCancel} style={{ marginRight: 8 }}>
                  {t("common.cancel")}
                </StyledButton>
                <StyledButton
                  $primary
                  onClick={handleSave}
                  disabled={!isSectionValid}
                >
                  {t("common.save")}
                </StyledButton>
              </div>
            ) : (
              <Button
                type="text"
                icon={<EditIcon />}
                onClick={() => {
                  setEditSection("settings");
                  form.setFieldsValue(getAgentFormData(agent));
                  // Reset variables to original agent data when entering edit mode
                  if (agent?.greeting_message) {
                    setGreetingVariables(
                      parseVariables(agent.greeting_message)
                    );
                  }
                  if (agent?.system_message) {
                    setSystemVariables(parseVariables(agent.system_message));
                  }
                  if (agent?.voicemail_message) {
                    setVoicemailVariables(parseVariables(agent.voicemail_message));
                  }
                  if (agent?.voice_configs?.ending_message) {
                    setEndCallVariables(
                      parseVariables(agent.voice_configs.ending_message)
                    );
                  } else {
                    setEndCallVariables([]);
                  }
                }}
              />
            )}
          </SectionHeader>
          {editSection === "settings" ? (
            <>
              <InfoGrid>
                <InfoRow>
                  <Label>{t("agents.agentForm.voice")}</Label>
                  <VoiceControls>
                    <Form.Item
                      name="voice"
                      style={{ width: 400, margin: 0 }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "agents.agentForm.validation.voiceRequired"
                          ),
                        },
                      ]}
                    >
                      <Select
                        size="large"
                        style={{ width: 400 }}
                        placeholder={t("agents.agentForm.selectVoice")}
                        onChange={(value) => handleInputChange("voice", value)}
                      >
                        {AGENTS_VOICE.map((voice, index) => (
                          <Option key={index} value={voice.value}>
                            {t(`agents.agentForm.${voice.name}Voice`)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <VoicePreviewButtonWrapper>
                      <VoicePreviewButton voiceType={currentVoiceName} />
                    </VoicePreviewButtonWrapper>
                  </VoiceControls>
                </InfoRow>
                <InfoRow>
                  <Label>Tools</Label>
                  <Form.Item
                    name="tool"
                    style={{ width: 400, margin: 0 }}
                    normalize={(value) =>
                      Array.isArray(value)
                        ? value.filter(
                            (fn: string | number) => String(fn) !== "endCall"
                          )
                        : value
                    }
                  >
                    <Select
                      mode="multiple"
                      size="large"
                      maxCount={10}
                      maxTagCount="responsive"
                      maxTagPlaceholder={(omittedValues) => (
                        <span>+{omittedValues.length}</span>
                      )}
                      style={{ width: 400 }}
                      placeholder={t("agents.agentForm.toolsPlaceholder")}
                      onChange={(value) => handleInputChange("tool", value)}
                    >
                      {TOOL_TYPE_GROUPS.map((group) => {
                        const list = groupedTools[group.value] || [];
                        // Only super admin users can see internal tools
                        if (
                          group.value === "internal" &&
                          !user?.is_super_admin
                        ) {
                          return null;
                        }
                        if (!list.length) return null;

                        const labelNode = (
                          <span style={{ fontSize: 14 }}>{group.label}</span>
                        );

                        return (
                          <OptGroup key={group.value} label={labelNode}>
                            {list
                              .filter(
                                (tool: any) => tool?.function_name !== "endCall"
                              )
                              .map((tool: any) => {
                                const isVerifyCustomer =
                                  tool?.function_name === "logCustomerInCall";
                                return (
                                  <Option
                                    key={tool._id}
                                    value={tool.function_name}
                                  >
                                    {isVerifyCustomer ? (
                                      <Tooltip title="HIPPA compliance - Verify the customer's identity by their birthday.">
                                        <span>{tool.display_name}</span>
                                      </Tooltip>
                                    ) : (
                                      tool.display_name
                                    )}
                                  </Option>
                                );
                              })}
                          </OptGroup>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </InfoRow>
              </InfoGrid>
            </>
          ) : (
            <InfoGrid>
              <InfoRow>
                <Label>{t("agents.agentForm.voice")}</Label>
                <Value>
                  {agent?.voice
                    ? t(
                        `agents.agentForm.${
                          AGENTS_VOICE.find(
                            (voice) => voice.value === agent?.voice
                          )?.name
                        }Voice`
                      )
                    : ""}
                </Value>
              </InfoRow>
              <InfoRow>
                <Label>{t("agents.agentForm.tools")}</Label>
                <Value>
                  {(agent?.tool || [])
                    .map((fn) => {
                      const tool = tools.find(
                        (t) => t.function_name === String(fn)
                      );
                      if (!tool) return null;
                      if (tool.function_name === "endCall") return null;
                      const type = String(tool.type || "").toLowerCase();
                      if (type === "internal" && !user?.is_super_admin) {
                        return null;
                      }
                      return tool.display_name;
                    })
                    .filter(Boolean)
                    .join(", ")}
                </Value>
              </InfoRow>
            </InfoGrid>
          )}
        </SectionCard>

        {/* Message Settings Section */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>
              {t("agents.agentDetail.messageSettings")}
            </SectionTitle>
            {editSection === "messages" ? (
              <div>
                <StyledButton onClick={handleCancel} style={{ marginRight: 8 }}>
                  {t("common.cancel")}
                </StyledButton>
                <StyledButton
                  $primary
                  onClick={handleSave}
                  disabled={!isSectionValid}
                >
                  {t("common.save")}
                </StyledButton>
              </div>
            ) : (
              <Button
                type="text"
                icon={<EditIcon />}
                onClick={() => {
                  setEditSection("messages");
                  form.setFieldsValue(getAgentFormData(agent));
                  // Reset variables to original agent data when entering edit mode
                  if (agent?.greeting_message) {
                    setGreetingVariables(
                      parseVariables(agent.greeting_message)
                    );
                  }
                  if (agent?.system_message) {
                    setSystemVariables(parseVariables(agent.system_message));
                  }
                  if (agent?.voicemail_message) {
                    setVoicemailVariables(parseVariables(agent.voicemail_message));
                  }
                  if (agent?.voice_configs?.ending_message) {
                    setEndCallVariables(
                      parseVariables(agent.voice_configs.ending_message)
                    );
                  } else {
                    setEndCallVariables([]);
                  }
                }}
              />
            )}
          </SectionHeader>
          {editSection === "messages" ? (
            <>
              <InfoGrid>
                <InfoRow>
                  <MessageSettingsLabel>
                    {t("agents.agentForm.greetingMessage")}
                  </MessageSettingsLabel>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="greeting_message"
                      style={{ margin: 0, marginBottom: 8 }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "agents.agentForm.validation.greetingMessageRequired"
                          ),
                        },
                      ]}
                    >
                      <Input.TextArea
                        size="large"
                        onChange={(e) =>
                          handleTextAreaChange("greeting_message", e)
                        }
                        onSelect={(e) => rememberCaret("greeting_message", e)}
                        onKeyUp={(e) => rememberCaret("greeting_message", e)}
                        onClick={(e) => rememberCaret("greeting_message", e)}
                        onBlur={(e) => rememberCaret("greeting_message", e)}
                        rows={3}
                        allowClear
                        placeholder={t("agents.agentForm.enterGreetingMessage")}
                        ref={greetingRef}
                      />
                    </Form.Item>
                    {/* Variable chips for greeting message */}
                    <ChipContainer>
                      {greetingVariables.map((v, idx) => (
                        <ChipButton key={`${v}-${idx}`}>
                          {v.length > 0 && <span>{v}</span>}
                          <ChipClose
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRemoveVariable("greeting_message", v);
                            }}
                          >
                            ×
                          </ChipClose>
                        </ChipButton>
                      ))}
                      <Dropdown
                        trigger={["click"]}
                        menu={getDropdownMenuForField("greeting_message")}
                      >
                        <ChipButton icon={"+"}>
                          {t("agents.agentForm.addVariable")}
                        </ChipButton>
                      </Dropdown>
                    </ChipContainer>
                  </div>
                </InfoRow>
                <InfoRow>
                  <MessageSettingsLabel>
                    {t("agents.agentForm.systemMessage")}
                  </MessageSettingsLabel>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="system_message"
                      style={{ margin: 0, marginBottom: 8 }}
                      rules={[
                        {
                          required: true,
                          message: t(
                            "agents.agentForm.validation.systemMessageRequired"
                          ),
                        },
                      ]}
                    >
                      <Input.TextArea
                        size="large"
                        onChange={(e) =>
                          handleTextAreaChange("system_message", e)
                        }
                        onSelect={(e) => rememberCaret("system_message", e)}
                        onKeyUp={(e) => rememberCaret("system_message", e)}
                        onClick={(e) => rememberCaret("system_message", e)}
                        onBlur={(e) => rememberCaret("system_message", e)}
                        rows={3}
                        allowClear
                        placeholder={t(
                          "agents.agentForm.systemMessagePlaceholder"
                        )}
                        ref={systemRef}
                      />
                    </Form.Item>
                    {/* Variable chips for system message */}
                    <ChipContainer>
                      {systemVariables.map((v, idx) => (
                        <ChipButton key={`${v}-${idx}`}>
                          {v.length > 0 && <span>{v}</span>}
                          <ChipClose
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRemoveVariable("system_message", v);
                            }}
                          >
                            ×
                          </ChipClose>
                        </ChipButton>
                      ))}
                      <Dropdown
                        trigger={["click"]}
                        menu={getDropdownMenuForField("system_message")}
                      >
                        <ChipButton key="add-system" icon={"+"}>
                          {t("agents.agentForm.addVariable")}
                        </ChipButton>
                      </Dropdown>
                    </ChipContainer>
                  </div>
                </InfoRow>
                <InfoRow>
                  <MessageSettingsLabel>
                    {t("agents.agentForm.voicemailMessage")}
                  </MessageSettingsLabel>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      name="voicemail_message"
                      style={{ margin: 0, marginBottom: 8 }}
                    >
                      <Input.TextArea
                        size="large"
                        rows={3}
                        allowClear
                        placeholder={t("agents.agentForm.enterVoicemailMessage")}
                        onChange={(e) =>
                          handleTextAreaChange("voicemail_message", e)
                        }
                        onSelect={(e) => rememberCaret("voicemail_message", e)}
                        onKeyUp={(e) => rememberCaret("voicemail_message", e)}
                        onClick={(e) => rememberCaret("voicemail_message", e)}
                        onBlur={(e) => rememberCaret("voicemail_message", e)}
                        ref={voicemailRef}
                      />
                    </Form.Item>
                    <ChipContainer>
                      {voicemailVariables.map((v, idx) => (
                        <ChipButton key={`${v}-${idx}`}>
                          {v.length > 0 && <span>{v}</span>}
                          <ChipClose
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRemoveVariable("voicemail_message", v);
                            }}
                          >
                            ×
                          </ChipClose>
                        </ChipButton>
                      ))}
                      <Dropdown
                        trigger={["click"]}
                        menu={getDropdownMenuForField("voicemail_message")}
                      >
                        <ChipButton key="add-voicemail" icon={"+"}>
                          {t("agents.agentForm.addVariable")}
                        </ChipButton>
                      </Dropdown>
                    </ChipContainer>
                  </div>
                </InfoRow>
                {user?.is_super_admin && (
                  <InfoRow>
                    <MessageSettingsLabel>
                      {t("agents.agentForm.endCallMessage")}
                    </MessageSettingsLabel>
                    <div style={{ flex: 1 }}>
                      <Form.Item
                        name="ending_message"
                        style={{ margin: 0, marginBottom: 8 }}
                      >
                        <Input.TextArea
                          size="large"
                          rows={3}
                          allowClear
                          placeholder={t("agents.agentForm.enterEndCallMessage")}
                          onChange={(e) =>
                            handleTextAreaChange("ending_message", e)
                          }
                          onSelect={(e) => rememberCaret("ending_message", e)}
                          onKeyUp={(e) => rememberCaret("ending_message", e)}
                          onClick={(e) => rememberCaret("ending_message", e)}
                          onBlur={(e) => rememberCaret("ending_message", e)}
                          ref={endCallRef}
                        />
                      </Form.Item>
                      <ChipContainer>
                        {endCallVariables.map((v, idx) => (
                          <ChipButton key={`${v}-${idx}`}>
                            {v.length > 0 && <span>{v}</span>}
                            <ChipClose
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleRemoveVariable("ending_message", v);
                              }}
                            >
                              ×
                            </ChipClose>
                          </ChipButton>
                        ))}
                        <Dropdown
                          trigger={["click"]}
                          menu={getDropdownMenuForField("ending_message")}
                        >
                          <ChipButton key="add-end-call" icon={"+"}>
                            {t("agents.agentForm.addVariable")}
                          </ChipButton>
                        </Dropdown>
                      </ChipContainer>
                    </div>
                  </InfoRow>
                )}
              </InfoGrid>
            </>
          ) : (
            <InfoGrid>
              <InfoRow>
                <Label style={{ alignSelf: "flex-start" }}>
                  {t("agents.agentForm.greetingMessage")}
                </Label>
                <Value style={{ whiteSpace: "pre-wrap" }}>
                  {agent?.greeting_message || ""}
                </Value>
              </InfoRow>
              <InfoRow>
                <Label style={{ alignSelf: "flex-start" }}>
                  {t("agents.agentForm.systemMessage")}
                </Label>
                <Value style={{ whiteSpace: "pre-wrap" }}>
                  {agent?.system_message || ""}
                </Value>
              </InfoRow>
              <InfoRow>
                <Label style={{ alignSelf: "flex-start" }}>
                  {t("agents.agentForm.voicemailMessage")}
                </Label>
                <Value style={{ whiteSpace: "pre-wrap" }}>
                  {agent?.voicemail_message || ""}
                </Value>
              </InfoRow>
              {user?.is_super_admin && (
                <InfoRow>
                  <Label style={{ alignSelf: "flex-start" }}>
                    {t("agents.agentForm.endCallMessage")}
                  </Label>
                  <Value style={{ whiteSpace: "pre-wrap" }}>
                    {agent?.voice_configs?.ending_message || ""}
                  </Value>
                </InfoRow>
              )}
            </InfoGrid>
          )}
        </SectionCard>

        {/* Knowledge Base Section */}
        <SectionCard style={{ marginTop: 24 }}>
          <SectionHeader>
            <SectionTitle>{t("agents.agentDetail.knowledgeBase")}</SectionTitle>
            {editSection === "knowledge" ? (
              <div>
                <StyledButton onClick={handleCancel} style={{ marginRight: 8 }}>
                  {t("common.cancel")}
                </StyledButton>
                <StyledButton
                  $primary
                  onClick={handleSave}
                  disabled={!isSectionValid}
                >
                  {t("common.save")}
                </StyledButton>
              </div>
            ) : (
              <Button
                type="text"
                icon={<EditIcon />}
                onClick={() => {
                  setEditSection("knowledge");
                  setSelectedBaseKnowledge(
                    (agent?.knowledge_base_ids || []).map((item: any) => item._id)
                  );
                }}
              />
            )}
          </SectionHeader>
          {editSection === "knowledge" ? (
            <InfoGrid>
              <InfoRow>
                <Label style={{ alignItems: "flex-start" }}>
                  {t("agents.createAgentModal.documents")}
                </Label>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <Form.Item name="knowledge_base_ids" style={{ margin: 0 }}>
                    <Select
                      size="large"
                      mode="multiple"
                      value={selectedBaseKnowledge}
                      placeholder={t(
                        "agents.createAgentModal.knowledgeBasePlaceholder"
                      )}
                      onChange={(value) => {
                        setSelectedBaseKnowledge(value);
                      }}
                    >
                      {baseKnowledge.map((file: any) => (
                        <Option key={file._id} value={file._id}>
                          {file.file_name || "-"}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {knowledgeFiles.length > 0 && (
                    <ChipContainer>
                      <UploadedFileInfoRow>
                        <CheckCircle />
                        <UploadedFileText>
                          {t("agents.createAgentModal.fileUploaded")}
                        </UploadedFileText>
                      </UploadedFileInfoRow>
                      {knowledgeFiles.map((kf, idx) => (
                        <ChipButton key={`${kf.name}-${idx}`}>
                          <span>{kf.name}</span>
                          <ChipClose onClick={() => removeKnowledgeFile(idx)}>
                            ×
                          </ChipClose>
                        </ChipButton>
                      ))}
                    </ChipContainer>
                  )}
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 24 }}
                  >
                    <Upload
                      name="files"
                      beforeUpload={() => false}
                      accept=".txt,.pdf,.docx,.doc"
                      multiple
                      fileList={[]}
                      showUploadList={false}
                      onChange={({ fileList }: any) => {
                        const files = fileList
                          .map((f: any) => f.originFileObj)
                          .filter(Boolean);
                        addKnowledgeFiles(files);
                      }}
                    >
                      <Button size="large" icon={<UploadOutlined />}>
                        {t("agents.createAgentModal.uploadFile")}
                      </Button>
                    </Upload>
                    <span style={{ color: theme.colors.grey1, fontSize: 14 }}>
                      {t("agents.createAgentModal.allowedFileTypes")}
                    </span>
                  </div>
                  <div
                    onDragOver={() => setIsKbDragging(true)}
                    onDragLeave={() => setIsKbDragging(false)}
                    onDrop={() => setIsKbDragging(false)}
                  >
                    <Upload.Dragger
                      name="files"
                      fileList={[]}
                      beforeUpload={() => false}
                      multiple
                      onChange={({ fileList }: any) => {
                        const files = fileList
                          .map((f: any) => f.originFileObj)
                          .filter(Boolean);
                        addKnowledgeFiles(files);
                      }}
                      accept=".txt,.pdf,.docx,.doc"
                      style={{
                        borderRadius: 8,
                        background: isKbDragging
                          ? theme.colors.green2
                          : theme.colors.grey4,
                        borderColor: isKbDragging
                          ? theme.colors.green1
                          : theme.colors.grey1,
                        borderWidth: 2,
                        transition: "all 0.2s",
                      }}
                      height={120}
                      showUploadList={false}
                    >
                      <div
                        style={{
                          color: isKbDragging
                            ? theme.colors.green1
                            : theme.colors.grey1,
                          fontSize: 15,
                          textAlign: "center",
                        }}
                      >
                        {t("campaignModal.create.dragDropText")}
                        <br />
                        {t("campaignModal.create.dragDropSubtext")}
                      </div>
                    </Upload.Dragger>
                  </div>
                </div>
              </InfoRow>
            </InfoGrid>
          ) : (
            <InfoGrid>
              <InfoRow>
                <Label style={{ alignItems: "flex-start" }}>
                  {t("agents.createAgentModal.documents")}
                </Label>
                <Value style={{ display: "block" }}>
                  {(agent?.knowledge_base_ids || []).length === 0 ? (
                    <span>-</span>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {(agent?.knowledge_base_ids || []).map(
                        (item: any, idx: number) => (
                          <a
                            key={`${item.presigned_url}-${idx}`}
                            href={item.presigned_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.file_name}
                          </a>
                        )
                      )}
                    </div>
                  )}
                </Value>
              </InfoRow>
            </InfoGrid>
          )}
        </SectionCard>
      </Form>

      {loading && (
        <LoadingOverlay>
          <Spin size="large" />
        </LoadingOverlay>
      )}
      <UnsavedChangesModal
        open={showUnsavedModal}
        onLeave={handleLeave}
        onCancel={handleUnsavedCancel}
        message={t("agents.agentDetail.areYouSureDiscardChanges")}
      />
    </Container>
  );
};

export default AgentDetail;

// StyledButton component matching CreateAgentModal
const StyledButton = styled(Button)<{ $primary?: boolean }>`
  background: ${({ $primary }) =>
    $primary ? theme.colors.green1 : theme.colors.grey3};
  color: ${({ $primary }) => ($primary ? "#fff" : "#000")};
  border: 1px solid
    ${({ $primary }) => ($primary ? theme.colors.green1 : theme.colors.grey2)};
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ $primary }) =>
      $primary ? theme.colors.green1 : theme.colors.grey3} !important;
    color: ${({ $primary }) => ($primary ? "#fff" : "#000")} !important;
    border: 1px solid
      ${({ $primary }) => ($primary ? theme.colors.green1 : theme.colors.grey2)} !important;
  }

  &:disabled {
    background: ${theme.colors.grey2} !important;
    color: #fff !important;
    border: 1px solid ${theme.colors.grey2} !important;
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: 100svh;
  margin: 0 auto;
  padding: 52px 72px;
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
`;

const BackLink = styled.div`
  color: #1890ff;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 16px;
  margin-top: 6px;
`;

const Text = styled.span`
  font-size: 16px;
  color: #8c98a4;
  margin-left: 16px;
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  height: 40px;
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${theme.colors.grey1};
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoRow = styled.div`
  display: flex;
`;

const Label = styled.div`
  width: 200px;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  display: flex;
`;

const MessageSettingsLabel = styled.div`
  width: 200px;
  font-size: 16px;
  font-weight: 400;
  display: flex;
`;

const Value = styled.div`
  color: #222;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  display: flex;
  flex: 1;
`;

const VoiceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const VoicePreviewButtonWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: ${theme.colors.grey3};
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(
    255,
    255,
    255,
    0.8
  ); /* Semi-transparent white background */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* Ensure it's above other content */
  gap: 16px;
`;

// Variable chip styled components
const ChipButton = styled(Button)`
  height: 28px;
  border-radius: 16px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.grey3};
  border: 1px solid ${theme.colors.grey2};
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const ChipClose = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  border-radius: 50%;
  line-height: 18px;
  cursor: pointer;
  margin-left: 8px;
  color: ${theme.colors.grey1};
  font-weight: bold;

  &:hover {
    color: #000;
  }
`;

// Upload UI small helpers reused from CreateAgentModal look
const UploadedFileInfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  height: 30px;
`;

const UploadedFileText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;
